import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import { getStoredUsername, saveStoredUsername } from '../../utils/usernameStorage';

// Badge configurations
const BADGES = {
  regular: { emoji: '🔥', label: 'Regular', days: 3, color: 'from-orange-400 to-red-500' },
  loyal: { emoji: '💎', label: 'Loyal Fan', days: 7, color: 'from-blue-400 to-purple-500' },
  hardcore: { emoji: '👑', label: 'Hardcore Fan', days: 30, color: 'from-yellow-400 to-amber-500' },
  legend: { emoji: '🏆', label: 'Legend', days: 100, color: 'from-purple-400 to-pink-500' },
};

// Question type labels
const QUESTION_TYPES = {
  classic: { label: 'Classic Dilemma', emoji: '⚡' },
  situation: { label: 'Situation', emoji: '🎯' },
  personality: { label: 'Fan Personality', emoji: '🎭' },
  prediction: { label: 'Prediction', emoji: '🔮' },
  reaction: { label: 'Reaction', emoji: '💬' },
};

const FunDilemma = () => {
  // User state
  const [user, setUser] = useState(null);
  const [showUsernamePrompt, setShowUsernamePrompt] = useState(false);
  const [usernameInput, setUsernameInput] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [creatingUser, setCreatingUser] = useState(false);

  // Dilemma state
  const [dilemmas, setDilemmas] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);
  const [error, setError] = useState(null);
  
  // Progress state
  const [progress, setProgress] = useState({ answered: 0, total: 5, isCompleted: false, agreementPercentage: 0, totalFansToday: 0 });
  
  // View state
  const [view, setView] = useState('game'); // 'game', 'results', 'leaderboard', 'history'
  const [leaderboardType, setLeaderboardType] = useState('streak');
  const [leaderboardData, setLeaderboardData] = useState({ leaderboard: [], currentUser: null });
  const [historyData, setHistoryData] = useState({ dilemmas: [], pagination: {} });
  
  // Animation state
  const [showConfetti, setShowConfetti] = useState(false);
  const [newBadge, setNewBadge] = useState(null);
  
  // Countdown
  const [countdown, setCountdown] = useState({ hours: 0, minutes: 0, seconds: 0 });

  // Calculate countdown to midnight
  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      
      const diff = tomorrow - now;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setCountdown({ hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  // Fetch today's dilemmas
  const fetchTodaysDilemmas = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/api/fun-dilemma/today`);
      
      setDilemmas(response.data.dilemmas || []);
      setProgress(response.data.progress || { answered: 0, total: 5, isCompleted: false });
      
      if (response.data.user) {
        setUser(response.data.user);
      }
      
      // Find first unanswered dilemma
      const firstUnanswered = (response.data.dilemmas || []).findIndex(d => !d.hasVoted);
      setCurrentIndex(firstUnanswered >= 0 ? firstUnanswered : 0);
      
      // If all completed, show results
      if (response.data.progress?.isCompleted) {
        setView('results');
      }
    } catch (err) {
      console.error('Error fetching dilemmas:', err);
      if (err.response?.status === 404) {
        setError('no-dilemma');
      } else {
        setError('fetch-error');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const createUserProfile = useCallback(async (rawUsername, options = {}) => {
    const username = rawUsername.trim();

    if (!username || username.length < 2) {
      if (!options.silent) {
        setUsernameError('Username must be at least 2 characters');
      }
      return false;
    }

    if (!options.silent) {
      setCreatingUser(true);
    }
    setUsernameError('');

    try {
      const response = await axios.post(`${API_BASE_URL}/api/fun-dilemma/user/create`, {
        username,
      });

      const savedUsername = saveStoredUsername(response.data.user?.username || username);
      setUser(response.data.user);
      setUsernameInput(savedUsername || username);
      setShowUsernamePrompt(false);

      if (options.refreshAfterCreate !== false) {
        fetchTodaysDilemmas();
      }

      return true;
    } catch (err) {
      const existingUser = err.response?.data?.user;

      if (existingUser?.username) {
        const savedUsername = saveStoredUsername(existingUser.username);
        setUser(existingUser);
        setUsernameInput(savedUsername || existingUser.username);
        setShowUsernamePrompt(false);

        if (options.refreshAfterCreate !== false) {
          fetchTodaysDilemmas();
        }

        return true;
      }

      setUsernameError(err.response?.data?.message || 'Failed to create username');
      setShowUsernamePrompt(true);
      return false;
    } finally {
      if (!options.silent) {
        setCreatingUser(false);
      }
    }
  }, [fetchTodaysDilemmas]);

  // Check user profile on mount
  const checkUserProfile = useCallback(async () => {
    const savedUsername = getStoredUsername();

    try {
      const response = await axios.get(`${API_BASE_URL}/api/fun-dilemma/user/profile`);
      if (response.data.exists) {
        const profileUsername = response.data.user?.username || savedUsername;
        if (profileUsername) {
          saveStoredUsername(profileUsername);
          setUsernameInput(profileUsername);
        }
        setUser(response.data.user);
        setShowUsernamePrompt(false);
      } else if (savedUsername) {
        setUsernameInput(savedUsername);
        const restored = await createUserProfile(savedUsername, { silent: true, refreshAfterCreate: false });
        if (!restored) {
          setShowUsernamePrompt(true);
        }
      } else {
        setShowUsernamePrompt(true);
      }
    } catch (err) {
      console.error('Error checking user profile:', err);
      if (savedUsername) {
        setUsernameInput(savedUsername);
      }
      setShowUsernamePrompt(true);
    }
  }, [createUserProfile]);

  // Create username
  const handleCreateUsername = async (e) => {
    e.preventDefault();
    await createUserProfile(usernameInput);
  };

  // Skip username (play anonymously)
  const handleSkipUsername = () => {
    setShowUsernamePrompt(false);
    fetchTodaysDilemmas();
  };

  // Vote on dilemma
  const handleVote = async (optionKey) => {
    if (voting || dilemmas[currentIndex]?.hasVoted) return;
    
    setVoting(true);
    
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/fun-dilemma/${dilemmas[currentIndex]._id}/vote`,
        { option: optionKey }
      );
      
      // Update local state
      const updatedDilemmas = [...dilemmas];
      updatedDilemmas[currentIndex] = {
        ...updatedDilemmas[currentIndex],
        hasVoted: true,
        userChoice: optionKey,
        options: response.data.results.options,
        totalVotes: response.data.results.totalVotes
      };
      setDilemmas(updatedDilemmas);
      
      // Update progress
      const newAnswered = progress.answered + 1;
      const isNowCompleted = response.data.completedToday;
      
      setProgress(prev => ({
        ...prev,
        answered: newAnswered,
        isCompleted: isNowCompleted
      }));
      
      // Update user if returned
      if (response.data.user) {
        setUser(prev => ({ ...prev, ...response.data.user }));
        
        // Check for new badges
        if (response.data.user.badges) {
          const userBadges = response.data.user.badges;
          if (userBadges.length > 0) {
            const latestBadge = userBadges[userBadges.length - 1];
            if (user?.badges && !user.badges.find(b => b.type === latestBadge.type)) {
              setNewBadge(latestBadge);
              setTimeout(() => setNewBadge(null), 5000);
            }
          }
        }
      }
      
      // Auto-advance after 2 seconds
      setTimeout(() => {
        if (isNowCompleted) {
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 3000);
          setView('results');
        } else if (currentIndex < dilemmas.length - 1) {
          setCurrentIndex(prev => prev + 1);
        }
      }, 2000);
      
    } catch (err) {
      console.error('Error voting:', err);
      if (err.response?.data?.message) {
        alert(err.response.data.message);
      }
    } finally {
      setVoting(false);
    }
  };

  // Fetch leaderboard
  const fetchLeaderboard = async (type = 'streak') => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/fun-dilemma/leaderboard/${type}`);
      setLeaderboardData(response.data);
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
    }
  };

  // Fetch history
  const fetchHistory = async (page = 1) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/fun-dilemma/history?page=${page}&limit=10`);
      setHistoryData(response.data);
    } catch (err) {
      console.error('Error fetching history:', err);
    }
  };

  // Share functionality
  const handleShare = async () => {
    const shareText = `🔥 I just completed today's Fun Dilemma!\n\n📊 Agreement with fans: ${progress.agreementPercentage}%\n🔥 My streak: ${user?.currentStreak || 0} days\n\nCan you beat me? Play now!`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Fun Dilemma - Daily Sports Opinion Game',
          text: shareText,
          url: window.location.href
        });
        
        // Grant share bonus
        try {
          await axios.post(`${API_BASE_URL}/api/fun-dilemma/share-bonus`);
          setUser(prev => prev ? { ...prev, skipProtection: Math.min((prev.skipProtection || 0) + 1, 2) } : prev);
        } catch (e) {
          console.log('Share bonus already granted');
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyToClipboard(shareText);
        }
      }
    } else {
      copyToClipboard(shareText);
    }
  };

  const copyToClipboard = (text) => {
    const fullText = `${text}\n\n${window.location.href}`;
    navigator.clipboard.writeText(fullText).then(() => {
      alert('Copied to clipboard! Share with your friends 📋');
    }).catch(() => {
      alert('Could not copy');
    });
  };

  // Initial load
  useEffect(() => {
    checkUserProfile();
    fetchTodaysDilemmas();
  }, [checkUserProfile, fetchTodaysDilemmas]);

  // Fetch leaderboard when view changes
  useEffect(() => {
    if (view === 'leaderboard') {
      fetchLeaderboard(leaderboardType);
    } else if (view === 'history') {
      fetchHistory(1);
    }
  }, [view, leaderboardType]);

  // Get option colors
  const getOptionColors = (index) => {
    const colors = [
      { bg: 'from-blue-500 to-blue-600', hover: 'from-blue-600 to-blue-700', text: 'blue', ring: '#3b82f6' },
      { bg: 'from-orange-500 to-red-500', hover: 'from-orange-600 to-red-600', text: 'orange', ring: '#f97316' },
      { bg: 'from-green-500 to-teal-500', hover: 'from-green-600 to-teal-600', text: 'green', ring: '#10b981' },
    ];
    return colors[index % colors.length];
  };

  // Username prompt modal
  if (showUsernamePrompt) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-500 via-rose-500 to-orange-400 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full animate-[bounce-in_0.5s_ease-out]">
          <div className="text-center mb-6">
            <div className="text-6xl mb-4">🎮</div>
            <h2 className="text-2xl font-black text-gray-800 mb-2">Choose Your Fan Name</h2>
            <p className="text-gray-600">This will be your identity across all games!</p>
          </div>
          
          <form onSubmit={handleCreateUsername}>
            <input
              type="text"
              value={usernameInput}
              onChange={(e) => setUsernameInput(e.target.value)}
              placeholder="Enter your fan name..."
              maxLength={20}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-pink-500 focus:outline-none text-lg text-center font-medium mb-3"
              autoFocus
            />
            
            {usernameError && (
              <p className="text-red-500 text-sm text-center mb-3">{usernameError}</p>
            )}
            
            <button
              type="submit"
              disabled={creatingUser}
              className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold py-3 rounded-xl hover:from-pink-600 hover:to-rose-600 transition-all mb-3 disabled:opacity-50"
            >
              {creatingUser ? 'Creating...' : "Let's Go! 🚀"}
            </button>
            
            <button
              type="button"
              onClick={handleSkipUsername}
              className="w-full text-gray-500 hover:text-gray-700 font-medium py-2"
            >
              Play Anonymously
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-500 via-rose-500 to-orange-400 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white text-xl font-bold">Loading today's dilemmas...</p>
        </div>
      </div>
    );
  }

  // Error states
  if (error === 'no-dilemma') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-500 via-rose-500 to-orange-400 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 text-center max-w-md border border-white/20">
          <div className="text-6xl mb-4">😴</div>
          <h2 className="text-2xl font-bold text-white mb-2">No Dilemmas Today</h2>
          <p className="text-white/80 mb-6">Check back tomorrow for new brain-teasing sports decisions!</p>
          <Link 
            to="/" 
            className="inline-block bg-white text-rose-500 font-bold text-sm sm:text-base py-2 sm:py-3 px-5 sm:px-8 rounded-full hover:bg-white/90 transition-all shadow-lg"
          >
            ← Back
          </Link>
        </div>
      </div>
    );
  }

  if (error === 'fetch-error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-500 via-rose-500 to-orange-400 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 text-center max-w-md border border-white/20">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-white mb-2">Oops!</h2>
          <p className="text-white/80 mb-6">Something went wrong. Please try again.</p>
          <button 
            onClick={fetchTodaysDilemmas}
            className="bg-white text-rose-500 font-bold py-3 px-8 rounded-full hover:bg-white/90 transition-all shadow-lg"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const currentDilemma = dilemmas[currentIndex];

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-500 via-rose-500 to-orange-400 relative overflow-hidden">
      {/* Confetti animation */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute"
              style={{
                left: `${Math.random() * 100}%`,
                animation: `confetti 3s ease-in-out forwards`,
                animationDelay: `${Math.random() * 2}s`,
                backgroundColor: ['#ff6b6b', '#feca57', '#48dbfb', '#ff9ff3', '#54a0ff'][Math.floor(Math.random() * 5)],
                width: '10px',
                height: '10px',
                borderRadius: Math.random() > 0.5 ? '50%' : '0',
              }}
            />
          ))}
        </div>
      )}

      {/* New badge notification */}
      {newBadge && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50" style={{ animation: 'slide-down 0.5s ease-out' }}>
          <div className={`bg-gradient-to-r ${BADGES[newBadge.type]?.color} text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3`}>
            <span className="text-3xl">{BADGES[newBadge.type]?.emoji}</span>
            <div>
              <p className="font-bold">New Badge Earned!</p>
              <p className="text-sm opacity-90">{BADGES[newBadge.type]?.label}</p>
            </div>
          </div>
        </div>
      )}

      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-orange-300/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-pink-300/20 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '0.5s' }}></div>
      </div>

      {/* Header */}
      <header className="relative z-10 pt-4 pb-2 px-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <Link 
            to="/" 
            className="inline-flex items-center border border-white/30 text-white hover:bg-white/10 hover:border-white/50 font-medium text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded transition-all backdrop-blur-sm"
          >
            <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="sm:hidden">Back</span>
            <span className="hidden sm:inline">Back to Home</span>
          </Link>
          
          {/* User info */}
          {user && (
            <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1.5">
              <span className="text-white font-medium text-sm">{user.username}</span>
              <span className="text-xl">🔥</span>
              <span className="text-white font-bold text-sm">{user.currentStreak}</span>
            </div>
          )}
          
          {/* Navigation buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => setView(view === 'leaderboard' ? 'game' : 'leaderboard')}
              className={`p-2 rounded-full transition-all ${view === 'leaderboard' ? 'bg-white text-pink-500' : 'bg-white/20 text-white hover:bg-white/30'}`}
            >
              🏆
            </button>
            <button
              onClick={() => setView(view === 'history' ? 'game' : 'history')}
              className={`p-2 rounded-full transition-all ${view === 'history' ? 'bg-white text-pink-500' : 'bg-white/20 text-white hover:bg-white/30'}`}
            >
              📜
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 px-4 pb-8">
        <div className="max-w-2xl mx-auto">
          
          {/* Title */}
          <div className="text-center mb-4">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-1.5 mb-2">
              <span className="text-xl">🤔</span>
              <span className="text-white font-bold text-sm uppercase tracking-wider">Fun Dilemma</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-black text-white drop-shadow-lg">
              Today's 5 Dilemmas
            </h1>
          </div>

          {/* Progress Bar */}
          {view === 'game' && (
            <div className="mb-4">
              <div className="flex items-center justify-between text-white/80 text-sm mb-2">
                <span>Progress: {progress.answered}/{progress.total}</span>
                <span className="flex items-center gap-1">
                  <span>⏰</span>
                  {String(countdown.hours).padStart(2, '0')}:{String(countdown.minutes).padStart(2, '0')}:{String(countdown.seconds).padStart(2, '0')}
                </span>
              </div>
              <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-white transition-all duration-500"
                  style={{ width: `${(progress.answered / progress.total) * 100}%` }}
                />
              </div>
              {/* Question dots */}
              <div className="flex justify-center gap-2 mt-3">
                {dilemmas.map((d, idx) => (
                  <button
                    key={d._id}
                    onClick={() => d.hasVoted && setCurrentIndex(idx)}
                    className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                      idx === currentIndex 
                        ? 'bg-white text-pink-500 scale-110' 
                        : d.hasVoted 
                          ? 'bg-green-400 text-white cursor-pointer hover:scale-105' 
                          : 'bg-white/30 text-white/70'
                    }`}
                  >
                    {d.hasVoted ? '✓' : idx + 1}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Game View */}
          {view === 'game' && currentDilemma && (
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden transform transition-all">
              {/* Question type badge */}
              <div className="bg-gradient-to-r from-rose-500 to-pink-500 p-3 flex items-center justify-center gap-2">
                <span className="text-xl">{QUESTION_TYPES[currentDilemma.questionType]?.emoji || '⚡'}</span>
                <span className="text-white font-bold uppercase tracking-wider text-sm">
                  {QUESTION_TYPES[currentDilemma.questionType]?.label || 'Dilemma'}
                </span>
              </div>

              {/* Question */}
              <div className="p-6">
                <h2 className="text-xl md:text-2xl font-black text-gray-800 text-center leading-tight mb-6">
                  {currentDilemma.question}
                </h2>

                {/* Options */}
                <div className="space-y-3">
                  {currentDilemma.options.map((option, idx) => {
                    const colors = getOptionColors(idx);
                    const optionKey = option.key || String(idx);
                    const isSelected = currentDilemma.userChoice === optionKey || currentDilemma.userChoice === String(idx);
                    
                    return (
                      <button
                        key={idx}
                        onClick={() => !currentDilemma.hasVoted && handleVote(optionKey)}
                        disabled={currentDilemma.hasVoted || voting}
                        className={`w-full relative overflow-hidden rounded-2xl transition-all duration-300 transform ${
                          !currentDilemma.hasVoted ? 'hover:scale-[1.02] active:scale-[0.98] cursor-pointer' : ''
                        }`}
                      >
                        <div 
                          className={`p-4 md:p-5 ${
                            currentDilemma.hasVoted 
                              ? 'bg-gray-100' 
                              : `bg-gradient-to-r ${colors.bg}`
                          }`}
                          style={{ 
                            boxShadow: isSelected ? `0 0 0 4px ${colors.ring}, 0 0 0 6px white` : undefined 
                          }}
                        >
                          {/* Progress bar for results */}
                          {currentDilemma.hasVoted && (
                            <div 
                              className={`absolute inset-0 bg-gradient-to-r ${colors.bg} opacity-80 transition-all duration-1000`}
                              style={{ width: `${option.percentage || 0}%` }}
                            />
                          )}
                          
                          <div className="relative z-10 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className={`w-9 h-9 rounded-full flex items-center justify-center font-bold ${
                                currentDilemma.hasVoted 
                                  ? `bg-gradient-to-r ${colors.bg} text-white` 
                                  : 'bg-white/20 text-white'
                              }`}>
                                {String.fromCharCode(65 + idx)}
                              </span>
                              <span className={`font-bold ${currentDilemma.hasVoted ? 'text-gray-800' : 'text-white'}`}>
                                {option.text}
                              </span>
                            </div>
                            
                            {currentDilemma.hasVoted && (
                              <div className="flex items-center gap-2">
                                <span className="text-xl font-black" style={{ color: colors.ring }}>
                                  {option.percentage || 0}%
                                </span>
                                {isSelected && (
                                  <span className={`bg-gradient-to-r ${colors.bg} text-white text-xs px-2 py-1 rounded-full`}>
                                    YOU
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Vote count */}
                {currentDilemma.hasVoted && (
                  <div className="mt-4 text-center text-gray-500 text-sm">
                    👥 {currentDilemma.totalVotes?.toLocaleString() || 0} fans voted
                  </div>
                )}

                {/* Voting instruction */}
                {!currentDilemma.hasVoted && !voting && (
                  <p className="text-center text-gray-400 mt-4 text-sm">
                    ☝️ Tap to choose • No right or wrong!
                  </p>
                )}

                {voting && (
                  <div className="text-center mt-4">
                    <div className="inline-flex items-center gap-2 text-gray-500">
                      <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-sm">Recording...</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Results View */}
          {view === 'results' && (
            <div className="space-y-4">
              {/* Completion Card */}
              <div className="bg-white rounded-3xl shadow-2xl p-6 text-center">
                <div className="text-6xl mb-4">🎉</div>
                <h2 className="text-2xl font-black text-gray-800 mb-2">All Done!</h2>
                <p className="text-gray-600 mb-4">You've completed today's dilemmas</p>
                
                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-4">
                    <div className="text-3xl font-black text-blue-600">{progress.agreementPercentage}%</div>
                    <div className="text-blue-600 text-sm font-medium">Agree with fans</div>
                  </div>
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-4">
                    <div className="text-3xl font-black text-orange-600">{progress.totalFansToday?.toLocaleString() || 0}</div>
                    <div className="text-orange-600 text-sm font-medium">Fans played today</div>
                  </div>
                </div>
                
                {/* User streak */}
                {user && (
                  <div className="bg-gradient-to-r from-pink-500 to-rose-500 rounded-2xl p-4 text-white mb-6">
                    <div className="flex items-center justify-center gap-3">
                      <span className="text-3xl">🔥</span>
                      <div>
                        <div className="text-2xl font-black">{user.currentStreak} Day Streak!</div>
                        <div className="text-white/80 text-sm">Best: {user.longestStreak} days</div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Badges */}
                {user?.badges && user.badges.length > 0 && (
                  <div className="mb-6">
                    <p className="text-gray-500 text-sm mb-2">Your Badges</p>
                    <div className="flex justify-center gap-2 flex-wrap">
                      {user.badges.map((badge, idx) => (
                        <div 
                          key={idx}
                          className={`bg-gradient-to-r ${BADGES[badge.type]?.color} text-white px-3 py-1.5 rounded-full flex items-center gap-1`}
                        >
                          <span>{BADGES[badge.type]?.emoji}</span>
                          <span className="text-sm font-medium">{BADGES[badge.type]?.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Share button */}
                <button
                  onClick={handleShare}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold py-4 rounded-2xl hover:from-purple-600 hover:to-pink-600 transition-all shadow-lg flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/>
                  </svg>
                  Share & Earn Skip Protection
                </button>
                
                {user?.skipProtection > 0 && (
                  <p className="text-green-600 text-sm mt-2">
                    🛡️ You have {user.skipProtection} skip protection(s)
                  </p>
                )}
              </div>
              
              {/* Review answers */}
              <button
                onClick={() => { setCurrentIndex(0); setView('game'); }}
                className="w-full bg-white/20 backdrop-blur-sm text-white font-bold py-3 rounded-2xl hover:bg-white/30 transition-all"
              >
                Review Your Answers
              </button>
            </div>
          )}

          {/* Leaderboard View */}
          {view === 'leaderboard' && (
            <div className="space-y-4">
              <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
                {/* Tabs */}
                <div className="flex border-b">
                  {[
                    { key: 'streak', label: '🔥 Streak', desc: 'Daily streak' },
                    { key: 'consistency', label: '📅 All-Time', desc: 'Total days' },
                    { key: 'weekly', label: '📊 Weekly', desc: 'This week' },
                  ].map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setLeaderboardType(tab.key)}
                      className={`flex-1 py-3 text-center font-bold text-sm transition-all ${
                        leaderboardType === tab.key 
                          ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white' 
                          : 'text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                
                {/* Leaderboard list */}
                <div className="p-4">
                  {leaderboardData.leaderboard.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No data yet. Be the first!</p>
                  ) : (
                    <div className="space-y-2">
                      {leaderboardData.leaderboard.map((entry, idx) => (
                        <div 
                          key={idx}
                          className={`flex items-center gap-3 p-3 rounded-xl ${
                            entry.isCurrentUser 
                              ? 'bg-gradient-to-r from-pink-100 to-rose-100 ring-2 ring-pink-400' 
                              : 'bg-gray-50'
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                            entry.rank === 1 ? 'bg-yellow-400 text-white' :
                            entry.rank === 2 ? 'bg-gray-300 text-white' :
                            entry.rank === 3 ? 'bg-amber-600 text-white' :
                            'bg-gray-200 text-gray-600'
                          }`}>
                            {entry.rank <= 3 ? ['🥇', '🥈', '🥉'][entry.rank - 1] : entry.rank}
                          </div>
                          <div className="flex-1">
                            <div className="font-bold text-gray-800">{entry.username}</div>
                            <div className="text-xs text-gray-500 flex items-center gap-1">
                              {entry.badges?.slice(0, 2).map((b, i) => (
                                <span key={i}>{BADGES[b.type]?.emoji}</span>
                              ))}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-black text-lg text-pink-600">
                              {leaderboardType === 'streak' && `${entry.streak}🔥`}
                              {leaderboardType === 'consistency' && `${entry.totalDays} days`}
                              {leaderboardType === 'weekly' && `${entry.daysActiveThisWeek}/7`}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Current user position if not in top */}
                  {leaderboardData.currentUser && !leaderboardData.leaderboard.find(e => e.isCurrentUser) && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-xs text-gray-500 mb-2">Your Position</p>
                      <div className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-pink-100 to-rose-100">
                        <div className="w-8 h-8 rounded-full bg-pink-400 text-white flex items-center justify-center font-bold text-sm">
                          {leaderboardData.currentUser.rank || '-'}
                        </div>
                        <div className="flex-1">
                          <div className="font-bold text-gray-800">{leaderboardData.currentUser.username}</div>
                        </div>
                        <div className="font-black text-pink-600">
                          {leaderboardType === 'streak' && `${leaderboardData.currentUser.streak}🔥`}
                          {leaderboardType === 'consistency' && `${leaderboardData.currentUser.totalDays} days`}
                          {leaderboardType === 'weekly' && `${leaderboardData.currentUser.daysActiveThisWeek}/7`}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <button
                onClick={() => setView('game')}
                className="w-full bg-white text-pink-500 font-bold py-3 rounded-2xl hover:bg-white/90 transition-all"
              >
                ← Back to Dilemmas
              </button>
            </div>
          )}

          {/* History View */}
          {view === 'history' && (
            <div className="space-y-4">
              <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
                <div className="bg-gradient-to-r from-rose-500 to-pink-500 p-4 text-center">
                  <h2 className="text-white font-bold text-lg">📜 Past Dilemmas</h2>
                </div>
                
                <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
                  {historyData.dilemmas.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No history yet!</p>
                  ) : (
                    historyData.dilemmas.map((d) => (
                      <div key={d._id} className="bg-gray-50 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2 text-xs text-gray-500">
                          <span>{new Date(d.dilemmaDate).toLocaleDateString()}</span>
                          <span>•</span>
                          <span>Q{d.dailySlot}</span>
                        </div>
                        <p className="font-bold text-gray-800 text-sm mb-2">{d.question}</p>
                        <div className="space-y-1">
                          {d.options.map((opt, idx) => {
                            const isUserChoice = d.userChoice === opt.key || d.userChoice === String(idx);
                            return (
                              <div key={idx} className="relative h-7 bg-gray-200 rounded overflow-hidden">
                                <div 
                                  className={`absolute inset-y-0 left-0 ${
                                    idx === 0 ? 'bg-blue-400' : idx === 1 ? 'bg-orange-400' : 'bg-green-400'
                                  }`}
                                  style={{ width: `${opt.percentage}%` }}
                                />
                                <div className="absolute inset-0 flex items-center justify-between px-2 text-xs">
                                  <span className={`font-medium ${isUserChoice ? 'text-white' : 'text-gray-700'}`}>
                                    {opt.text} {isUserChoice && '✓'}
                                  </span>
                                  <span className="font-bold text-gray-700">{opt.percentage}%</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="text-xs text-gray-400 mt-2">{d.totalVotes} votes</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
              
              <button
                onClick={() => setView('game')}
                className="w-full bg-white text-pink-500 font-bold py-3 rounded-2xl hover:bg-white/90 transition-all"
              >
                ← Back to Dilemmas
              </button>
            </div>
          )}

          {/* Fun fact */}
          {view === 'game' && !progress.isCompleted && (
            <div className="mt-4 bg-white/10 backdrop-blur-sm rounded-2xl p-3 text-center">
              <p className="text-white/80 text-sm">
                💡 Complete all 5 dilemmas daily to build your streak!
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 text-center py-4 text-white/60 text-sm">
        <p>Made with ❤️ for sports fans</p>
      </footer>

      {/* Global CSS for animations */}
      <style>{`
        @keyframes confetti {
          0% { transform: translateY(-100vh) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        @keyframes slide-down {
          0% { transform: translate(-50%, -100px); opacity: 0; }
          100% { transform: translate(-50%, 0); opacity: 1; }
        }
        @keyframes bounce-in {
          0% { transform: scale(0.8); opacity: 0; }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default FunDilemma;
